import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'

/** Maximum allowed nesting depth for pages (0-indexed: depth 0 = root, depth 4 = 5th level) */
export const MAX_PAGE_DEPTH = 4

/**
 * Check if a page can have sub-pages created under it.
 * Requires: title is not empty and depth < MAX_PAGE_DEPTH.
 */
export function canCreateSubPage(page: PageDocument, depth: number): boolean {
  if (depth >= MAX_PAGE_DEPTH) return false
  const title = page.title?.trim()
  if (!title) return false
  return true
}

/**
 * Get the depth of a page in the tree by walking up parentPageId.
 */
export function getPageDepth(pages: Array<PageDocument>, pageId: string): number {
  let depth = 0
  let current = pages.find((p) => p.id === pageId)
  while (current?.parentPageId) {
    depth++
    current = pages.find((p) => p.id === current!.parentPageId)
  }
  return depth
}

export interface PageTreeNode {
  page: PageDocument
  children: Array<PageTreeNode>
}

export interface FlatTreeItem {
  node: PageTreeNode
  depth: number
}

export function buildTree(pages: Array<PageDocument>, parentId: string | null = null): Array<PageTreeNode> {
  return pages
    .filter((p) => (p.parentPageId || null) === parentId)
    .sort((a, b) => {
      if (a.sortOrder && b.sortOrder) return a.sortOrder.localeCompare(b.sortOrder)
      return a.createdOn - b.createdOn
    })
    .map((page) => ({
      page,
      children: buildTree(pages, page.id),
    }))
}

/** Collect IDs of all nodes that have children. */
export function collectParentIds(nodes: Array<PageTreeNode>): Set<string> {
  const ids = new Set<string>()
  for (const node of nodes) {
    if (node.children.length > 0) {
      ids.add(node.page.id)
      for (const id of collectParentIds(node.children)) ids.add(id)
    }
  }
  return ids
}

export function flattenTree(
  nodes: Array<PageTreeNode>,
  depth: number,
  collapsedIds: Set<string>,
): Array<FlatTreeItem> {
  const result: Array<FlatTreeItem> = []
  for (const node of nodes) {
    result.push({ node, depth })
    if (!collapsedIds.has(node.page.id) && node.children.length > 0) {
      result.push(...flattenTree(node.children, depth + 1, collapsedIds))
    }
  }
  return result
}
